import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { withStyles } from 'tss-react/mui';
import { Typography, Divider, Fade, Button } from '@mui/material';

import withDataResolver from '../common/withDataResolver';
import {
  useInitData,
  useLoadAvailableResultfields,
  useLoadMassActions,
} from 'src/components/massAction/state';

import MassActionRequestList from './requestList/MassActionRequestList';

import MassActionHistory from './history/MassActionHistory';

import { Redirect, Route, Switch, useLocation, useRoute } from 'wouter';
import MassActionEditor from './editor/MassActionEditor';

const styles = () => ({
  root: {
    flex: 1,
    padding: '0',
    overflow: 'hidden',
    position: 'relative',
  },
});

function MassActionContentRender(props) {
  const [partialRoute, partialRouteParams] = useRoute('/oneoff/:id');
  const { t } = useTranslation();

  if (partialRoute && /^\d+$/.test(partialRouteParams.id)) {
    return <Redirect to={`/oneoff/${partialRouteParams.id}/edit`} />;
  }

  return (
    <Switch>
      <Route path="/oneoff/list">
        <MassActionRequestList {...props} />;
      </Route>
      <Route path="/oneoff/create">
        <MassActionEditor {...props} />;
      </Route>
      <Route path="/oneoff/:id/edit">
        <MassActionEditor {...props} />;
      </Route>
      <Route path="/oneoff/:id/history">
        <MassActionHistory {...props} />
      </Route>
      <Route path="/oneoff/*?">
        <Redirect to="/oneoff/list" />
      </Route>
      <Route>
        <div>{t('error_short')}</div>
      </Route>
    </Switch>
  );
}

const resolvers = {
  resolve: props => async () => {
    await props.doLoadData();
    await props.loadResultfields();
    await props.doLoadMassActions();
  },

  onResolved: () => async () => {},
  getLoaderText: () => i18next.t('smartmessaging.massAction.mainDataLoading'),
};

const MassActionContentWithData = withDataResolver(resolvers)(MassActionContentRender);

const BackBtn = ({ onBack }) => {
  const { t } = useTranslation();

  return (
    <Button
      variant="text"
      style={{
        textTransform: 'none',
      }}
      onClick={e => {
        e.preventDefault();
        onBack();
      }}
    >
      <Typography variant="body2" color="textSecondary">
        {t('smartmessaging.buttonLabel.backToList')}
      </Typography>
    </Button>
  );
};

BackBtn.propTypes = { onBack: PropTypes.func.isRequired };

function MassActionContent({ doClearData, ...props }) {
  const doLoadData = useInitData();
  const loadResultfields = useLoadAvailableResultfields();
  const doLoadMassActions = useLoadMassActions();
  const [, navigate] = useLocation();
  const [matchListRoute] = useRoute('/oneoff/list');
  const { t } = useTranslation();

  return (
    <>
      <div style={{ display: 'flex', padding: '10px', minHeight: '50px' }}>
        <Typography
          variant="h5"
          color="textSecondary"
          style={{
            margin: 'auto 16px',
            flex: 1,
            textAlign: 'left',
          }}
        >
          {t('smartmessaging.massAction.mainTitle')}
        </Typography>
        {!matchListRoute && (
          <BackBtn
            onBack={() => {
              doClearData();
              navigate('/oneoff/list');
            }}
          />
        )}
      </div>
      <Divider />
      <Fade in timeout={500}>
        <div className={props.classes.root}>
          <MassActionContentWithData
            {...props}
            doLoadData={doLoadData}
            loadResultfields={loadResultfields}
            doLoadMassActions={doLoadMassActions}
          />
        </div>
      </Fade>
    </>
  );
}

MassActionContent.propTypes = {
  classes: PropTypes.object.isRequired,
  doClearData: PropTypes.func.isRequired,
};

export default withStyles(MassActionContent, styles);
